import { useState, useCallback, useRef } from 'react';

/**
 * Generic wrapper for API call functions that handles loading/error state.
 * Reduces boilerplate in domain hooks (useDeals, useActivities, etc.).
 *
 * Usage:
 *   const [createDeal, loading, error] = useApiAction(async (apiCall, data) => {
 *     return apiCall('deals-create', { method: 'POST', body: JSON.stringify(data) });
 *   }, apiCall);
 */
export function useApiAction(fn, apiCall) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Use a ref so that execute has a stable identity even when callers pass
  // inline arrow functions as fn (which create a new reference every render).
  const fnRef = useRef(fn);
  fnRef.current = fn;

  const execute = useCallback(async (...args) => {
    setLoading(true);
    setError(null);
    try {
      const result = await fnRef.current(apiCall, ...args);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  return [execute, loading, error];
}
